// Where the console keeps the invitation token while its owner signs in or creates an account
// (spec/07 §6.5, ADR-0046). The link is `/invitations/accept#<token>`: the token is after `#` on purpose,
// so it never reaches a server log, a proxy or a `Referer` (`access/tokens.ts`'s `acceptUrlFor`) — read
// once from the fragment here, and only ever sent in a request body.
//
// A visitor with no account leaves the accept page to create one, and reading the email code often means
// switching to another tab and back, or opening the console in a new one. So the token is kept in
// `localStorage` — this browser only, never sent anywhere but the invitation endpoints — **with the
// invitation's expiry**, and forgotten once it is accepted, refused for good, found unusable, or past that
// expiry, and **at every sign-out** (`forgetInvitationOnSignOut`): a front desk's computer is shared, and the
// next person to sign in there must not be sent to somebody else's invitation. The accept page's own "Sign
// out and use that one" is the one sign-out that keeps it, because it is the invited person's way in.
// Nothing else is stored: not the invited address, not the organisation; the accept page reads those from
// the api with the token each time.

import type { ApiClient } from '@clavian/api-client';

const STORAGE_KEY = 'clavian:invitation';

/** How long a token is kept when its expiry is not known yet: an invitation's whole life, seven days. */
const DEFAULT_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;

interface Stored {
  readonly token: string;
  /** When the invitation stops working, as epoch milliseconds: past it, the token is forgotten. */
  readonly expiresAt: number;
}

function storage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    // A private window, or storage blocked: the token still works from the fragment alone, just
    // not across a sign-in or sign-up round trip.
    return undefined;
  }
}

function read(now: number): Stored | undefined {
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined || raw === '') return undefined;
    const parsed = JSON.parse(raw) as Partial<Stored>;
    if (typeof parsed.token !== 'string' || parsed.token === '' || typeof parsed.expiresAt !== 'number') return undefined;
    if (parsed.expiresAt <= now) {
      storage()?.removeItem(STORAGE_KEY);
      return undefined;
    }
    return { token: parsed.token, expiresAt: parsed.expiresAt };
  } catch {
    return undefined;
  }
}

function write(stored: Stored): void {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage full or blocked: the fragment still carries the token on this page.
  }
}

/** The token from the URL's fragment, or — once that is gone — the one this browser kept, while it lasts. */
export function readInvitationToken(hash: string, now: number = Date.now()): string | undefined {
  const fromHash = hash.replace(/^#/, '').trim();
  if (fromHash !== '') {
    const kept = read(now);
    // A new link replaces whatever was kept; the same link keeps the expiry already learnt.
    write({ token: fromHash, expiresAt: kept?.token === fromHash ? kept.expiresAt : now + DEFAULT_LIFETIME_MS });
    return fromHash;
  }
  return read(now)?.token;
}

/** The token kept for an invitation still waiting to be accepted, if there is one. */
export function pendingInvitationToken(now: number = Date.now()): string | undefined {
  return read(now)?.token;
}

/**
 * Once the api has said when the invitation expires, the token is kept exactly that long. Also how the
 * accept page keeps it through its own "Sign out and use that one", after the sign-out forgot it.
 */
export function rememberInvitationExpiry(token: string, expiresAt: string): void {
  const at = Date.parse(expiresAt);
  if (Number.isNaN(at)) return;
  const kept = read(Date.now());
  if (kept !== undefined && kept.token !== token) return;
  write({ token, expiresAt: at });
}

/** Where to go once signed in: back into the flow that sent the visitor there, or to the invitation waiting, or home. */
export function afterSignIn(redirect: string | undefined): string {
  if (redirect !== undefined) return redirect;
  return pendingInvitationToken() === undefined ? '/' : '/invitations/accept';
}

/** Accepted, refused for good, abandoned for a token that no longer names anything usable, or signed out. */
export function clearInvitationToken(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing kept, or nothing reachable: either way nothing is left behind here.
  }
}

/**
 * The console's api client, with every sign-out forgetting the kept invitation first — the shell's, the
 * step-up screen's, HQ's "not for your account", the accept page's own, and any added later — so none can
 * leave an invitation behind for the next person to sign in on this computer. Forgotten before the request,
 * so a sign-out that fails on the network still forgets it.
 */
export function forgetInvitationOnSignOut(api: ApiClient): ApiClient {
  return {
    ...api,
    signOut: (...args: Parameters<ApiClient['signOut']>) => {
      clearInvitationToken();
      return api.signOut(...args);
    },
  };
}
